/**
 * Usage, Budgets & Billing — read-only ACU usage + budget headroom.
 *
 * Reads GET /api/agentic/usage (backend/app/routes/agentic_fleet.py): ACU
 * totals + customer-facing amount from the immutable usage ledger, per-agent
 * breakdown, and the tenant budget policy. Provider cost is platform-only and
 * never exposed here. On 403/404 → NotEnabled.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Gauge } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  KpiCard,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getUsage } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function money(n: number): string {
  return `$${n.toFixed(2)}`;
}

function acu(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function UsagePage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'usage'],
    queryFn: getUsage,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="usage-loading">
        <Spinner size="lg" label="Loading usage" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load usage"
          description="Something went wrong while loading usage data. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { totals, by_agent, budget } = data;
  const util = budget?.utilization_pct ?? null;
  const barPct = util != null ? Math.min(100, util) : null;
  const overBudget = util != null && util > 100;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Usage, Budgets & Billing"
          description="Agent Compute Unit (ACU) consumption and budget headroom for your tenant. Read-only; provider costs are not shown."
        />

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard label="ACUs used" value={acu(totals.acu_units)} />
            <KpiCard label="Billable amount" value={money(totals.customer_amount)} />
            <KpiCard label="Usage events" value={totals.event_count} />
          </div>
        </Section>

        {budget && (
          <Section>
            <Card padding="md">
              <Stack gap="sm">
                <div className="flex items-center gap-2">
                  <Icon as={Gauge} size="sm" className="text-primary" />
                  <Heading level={2} className="text-base">
                    Budget ({humanize(budget.period)})
                  </Heading>
                </div>
                <Text variant="body-sm" tone={overBudget ? 'danger' : 'muted'}>
                  {acu(totals.acu_units)} of {acu(budget.included_acu)} included ACUs used
                  {util != null ? ` (${util}%)` : ''} · hard limit {acu(budget.hard_limit_acu)} ·
                  overage {budget.overage_allowed ? 'allowed' : 'off'}
                </Text>
                {barPct != null && (
                  <div
                    className="h-2 w-full overflow-hidden rounded-full bg-elevated"
                    role="progressbar"
                    aria-label={`Budget utilization: ${Math.round(util ?? 0)}%`}
                    aria-valuenow={Math.round(util ?? 0)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className={overBudget ? 'h-full bg-danger' : 'h-full bg-primary'}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                )}
              </Stack>
            </Card>
          </Section>
        )}

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Usage by agent
            </Heading>
            {by_agent.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  compact
                  icon={<Icon as={Gauge} size="lg" />}
                  title="No usage recorded yet"
                  description="When agents consume ACUs, per-agent usage appears here."
                />
              </Card>
            ) : (
              <Card padding="none">
                <table className="w-full text-left text-sm" data-testid="usage-by-agent">
                  <thead>
                    <tr className="text-muted">
                      <th className="px-4 py-2 font-medium">Agent</th>
                      <th className="px-4 py-2 font-medium">ACUs</th>
                      <th className="px-4 py-2 font-medium">Amount</th>
                      <th className="px-4 py-2 font-medium">Events</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {by_agent.map(a => (
                      <tr key={a.agent_id}>
                        <td className="px-4 py-2">{humanize(a.agent_id)}</td>
                        <td className="px-4 py-2">{acu(a.acu_units)}</td>
                        <td className="px-4 py-2">{money(a.customer_amount)}</td>
                        <td className="px-4 py-2">{a.event_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
